import React, { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ColDef, ValueFormatterParams, GridReadyEvent } from 'ag-grid-enterprise'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import { Voucher } from '../../../../pages/reporting/vouchers/types'
import { parseDate } from '../../../../config/dayjsConfig'
import { formatValueByType } from '../../../../utils/helpers'
import TransactionDetailRenderer from './TransactionDetailRenderer'

export interface VoucherPreviewTableProps {
  vouchers: Voucher[]
  height?: number | string
}

const dateValueFormatter = (params: ValueFormatterParams): string => {
  const date = params.value
  return date ? parseDate(date)?.format('DD.MM.YYYY') || '' : ''
}

const currencyValueFormatter = (params: ValueFormatterParams): string => {
  return formatValueByType(params.value, undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  })
}

const VoucherPreviewTable: React.FC<VoucherPreviewTableProps> = ({ vouchers, height = 400 }) => {
  const { t } = useTranslation()

  const columns = useMemo(
    (): ColDef<Voucher>[] => [
      {
        headerName: t('vouchersPage:voucherGuid'),
        field: 'guid',
        width: 150,
        cellRenderer: 'agGroupCellRenderer'
      },
      {
        headerName: t('vouchersPage:voucherType'),
        field: 'type',
        width: 150
      },
      {
        headerName: t('vouchersPage:voucherDate'),
        field: 'date',
        width: 150,
        valueFormatter: dateValueFormatter
      },
      {
        headerName: 'Debet',
        field: 'debit',
        width: 130,
        type: 'numericColumn',
        valueFormatter: currencyValueFormatter
      },
      {
        headerName: 'Kredit',
        field: 'credit',
        width: 130,
        type: 'numericColumn',
        valueFormatter: currencyValueFormatter
      },
      {
        headerName: t('vouchersPage:voucherDescription'),
        field: 'description',
        flex: 1,
        minWidth: 150
      }
    ],
    [t]
  )

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
      suppressHeaderMenuButton: true
    }),
    []
  )

  const onGridReady = useCallback((event: GridReadyEvent) => {
    // Expand all rows by default
    event.api.forEachNode(node => node.setExpanded(true))
  }, [])

  return (
    <div style={{ height }}>
      <CustomAgGrid
        columnDefs={columns}
        defaultColDef={defaultColDef}
        rowData={vouchers}
        getRowId={params => String(params.data.id || params.data.guid)}
        onGridReady={onGridReady}
        masterDetail
        detailCellRenderer={TransactionDetailRenderer}
        detailRowAutoHeight
        domLayout={vouchers.length <= 5 ? 'autoHeight' : undefined}
      />
    </div>
  )
}

export default VoucherPreviewTable
